import { useMemo } from 'react';

type Unit = 'metric' | 'imperial' | 'nautical';

type Props = {
  latitude: number;
  zoom: number;
  unit: Unit;
  style?: React.CSSProperties;
  maxWidth?: number;
};

const EARTH_CIRCUMFERENCE_METERS = 40075016.686;
const FEET_PER_METER = 3.28084;
const METERS_PER_MILE = 1609.344;
const METERS_PER_NAUTICAL_MILE = 1852;

function getRoundNum(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  const exponent = Math.floor(Math.log10(value));
  const scale = 10 ** exponent;
  const normalized = value / scale;

  const rounded =
    normalized >= 10 ? 10 : normalized >= 5 ? 5 : normalized >= 3 ? 3 : normalized >= 2 ? 2 : 1;

  return rounded * scale;
}

function formatNumber(value: number): string {
  if (value >= 100) return String(Math.round(value));
  if (value >= 10) return String(Math.round(value * 10) / 10);
  return String(Math.round(value * 100) / 100);
}

function metersPerPixelAtLatitude(latitude: number, zoom: number): number {
  const latCos = Math.cos((latitude * Math.PI) / 180);
  return (EARTH_CIRCUMFERENCE_METERS * latCos) / 2 ** (zoom + 8);
}

export default function ScaleControl({ latitude, zoom, unit, style, maxWidth = 100 }: Props) {
  const scaleInfo = useMemo(() => {
    const metersPerPixel = metersPerPixelAtLatitude(latitude, zoom);
    if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) {
      return null;
    }

    const maxMeters = metersPerPixel * maxWidth;

    if (unit === 'imperial') {
      const maxFeet = maxMeters * FEET_PER_METER;
      if (maxFeet > 5280) {
        const miles = getRoundNum(maxFeet / 5280);
        const meters = miles * METERS_PER_MILE;
        return {
          width: Math.round(meters / metersPerPixel),
          label: `${formatNumber(miles)} mi`,
        };
      }

      const feet = getRoundNum(maxFeet);
      const meters = feet / FEET_PER_METER;
      return {
        width: Math.round(meters / metersPerPixel),
        label: `${formatNumber(feet)} ft`,
      };
    }

    if (unit === 'nautical') {
      const nauticalMiles = getRoundNum(maxMeters / METERS_PER_NAUTICAL_MILE);
      if (nauticalMiles >= 1) {
        const meters = nauticalMiles * METERS_PER_NAUTICAL_MILE;
        return {
          width: Math.round(meters / metersPerPixel),
          label: `${formatNumber(nauticalMiles)} nm`,
        };
      }

      const meters = getRoundNum(maxMeters);
      return {
        width: Math.round(meters / metersPerPixel),
        label: `${formatNumber(meters)} m`,
      };
    }

    const meters = getRoundNum(maxMeters);
    if (meters >= 1000) {
      return {
        width: Math.round(meters / metersPerPixel),
        label: `${formatNumber(meters / 1000)} km`,
      };
    }

    return {
      width: Math.round(meters / metersPerPixel),
      label: `${formatNumber(meters)} m`,
    };
  }, [latitude, zoom, unit, maxWidth]);

  if (scaleInfo == null || scaleInfo.width <= 0) {
    return null;
  }

  return (
    <div className="mapboxgl-ctrl mapboxgl-ctrl-scale" style={{ ...style, width: scaleInfo.width }}>
      {scaleInfo.label}
    </div>
  );
}
